import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { OAuthService } from 'angular-oauth2-oidc';

import { config } from './../app/authConfig';

import { ClaimsEnum } from '../models/claims.enum';
import { LoadingIndicatorService } from './loading-indicator.service';
import { filter } from 'rxjs/operators';
import { BehaviorSubject } from 'rxjs';

@Injectable()
export class AuthorizationService {

    private isAuthenticatedSubject = new BehaviorSubject<boolean>(false);
    isAuthenticated$ = this.isAuthenticatedSubject.asObservable

    isAdmin: boolean = false;
    isCorpSystemsUser: boolean = false;
    isApprover: boolean = false;
    isRequester: boolean = false;

    private cachedRoles: string[] = null;
    private cachedUserName: string = null;

    constructor(private oAuthService: OAuthService, private router: Router, private loadingIndicatorService: LoadingIndicatorService) {
        this.oAuthService.configure(config);
        this.setupSessionPersistence();
        this.oAuthService.timeoutFactor = 0.3;
        this.oAuthService.setupAutomaticSilentRefresh();
        this.oAuthService.loadDiscoveryDocumentAndTryLogin().then(() => {
            if (!this.authenticated()) {
                this.login();
            }
            else {
                this.isAuthenticatedSubject.next(true);
            }
        });
        this.oAuthService.events.pipe(filter(e => e.type === 'token_received')).subscribe(() => {
            this.isAuthenticatedSubject.next(true);
        });

        this.oAuthService.events.pipe(filter(e => e.type === 'token_validation_error')).subscribe(() => {
            this.isAuthenticatedSubject.next(false);
        });
    }

    private setupSessionPersistence(): void {
        this.oAuthService.setStorage(localStorage);
        const token = this.oAuthService.getAccessToken();
        if (token && this.oAuthService.hasValidAccessToken()) {
            this.isAuthenticatedSubject.next(true);
        }
        else {
            this.isAuthenticatedSubject.next(false);
        }
    }

    authenticated(): boolean {
        return !!this.oAuthService.hasValidIdToken() && !!this.oAuthService.hasValidAccessToken();
    }

    checkUserAccess(isFullAccessRequired: boolean = false): void {
        if (isFullAccessRequired && !this.isInRole()) {
            alert('You are not authorized to use this page due to your access level.');
            this.router.navigate(['unauthorized']);
        }
    }

    dropCache(): void {
        this.cachedRoles = null;
        this.cachedUserName = null;
    }

    getAccessToken(): string {
        return this.oAuthService.getAccessToken();
    }

    getName(): string {
        return this.oAuthService.getIdentityClaims()?.['name'] ?? '';
    }

    getRoles(): string[] {
        if (!this.cachedRoles || this.cachedRoles.length == 0) {
            this.cachedRoles = this.oAuthService.getIdentityClaims()?.['roles'] ?? [];
        }
        return this.cachedRoles;
    }

    getPulteUsername(): string {
        return 'PHCORP\\' + this.getUsername().replace('@pulte.com', '').replace('@pultegroup.com', '');
    }

    getUsername(): string {
        if (this.cachedUserName == null) {
            this.cachedUserName = this.oAuthService.getIdentityClaims()?.['preferred_username'] ?? '';
        }

        return this.cachedUserName;
    }

    isAdminUser(): boolean {
        let retval = false;
        this.isAdmin = false;

        if (!this.cachedRoles || this.cachedRoles.length == 0) {
            this.cachedRoles = this.oAuthService.getIdentityClaims()?.['roles'] ?? [];
        }

        this.cachedRoles.forEach(element => {
            if (element?.includes(ClaimsEnum.LegalAdAdmin)) {
                retval = true;
                this.isAdmin = true;
            }
        });

        return retval;
    }

    isCorpSysUser(): boolean {
        let retval = false;
        this.isCorpSystemsUser = false;

        if (!this.cachedRoles || this.cachedRoles.length == 0) {
            this.cachedRoles = this.oAuthService.getIdentityClaims()?.['roles'] ?? [];
        }

        this.cachedRoles.forEach(element => {
            if (element?.includes(ClaimsEnum.LegalAdCorpSystems)) {
                retval = true;
                this.isCorpSystemsUser = true;
            }
        });

        return retval;
    }

    isApproveUser(): boolean {
        let retval = false;
        this.isApprover = false;

        if (!this.cachedRoles || this.cachedRoles.length == 0) {
            this.cachedRoles = this.oAuthService.getIdentityClaims()?.['roles'] ?? [];
        }

        this.cachedRoles.forEach(element => {
            if (element?.includes(ClaimsEnum.LegalAdApprove)) {
                retval = true;
                this.isApprover = true;
            }
        });

        return retval;
    }


    isRequestUser(): boolean {
        let retval = false;
        this.isRequester = false;

        if (!this.cachedRoles || this.cachedRoles.length == 0) {
            this.cachedRoles = this.oAuthService.getIdentityClaims()?.['roles'] ?? [];
        }

        this.cachedRoles.forEach(element => {
            if (element?.includes(ClaimsEnum.LegalAdRequest)) {
                retval = true;
                this.isRequester = true;
            }
        });

        return retval;
    }

    isInRole(): boolean {
        let retval = false;

        if (!this.cachedRoles || this.cachedRoles.length == 0) {
            this.cachedRoles = this.oAuthService.getIdentityClaims()?.['roles'] ?? [];
        }

        this.cachedRoles.forEach(element => {
            if (element?.includes(ClaimsEnum.LegalAdAdmin) || element?.includes(ClaimsEnum.LegalAdCorpSystems) || element?.includes(ClaimsEnum.LegalAdApprove) || element?.includes(ClaimsEnum.LegalAdRequest)) {
                retval = true;
            }
        });

        return retval;
    }

    login(): void {
        this.oAuthService.initLoginFlow();
    }

    logout(): void {
        this.oAuthService.logOut();
    }
}
